import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  applyRecast,
  applyStoryProposal,
  discardRecast,
  fixModule,
  requestRecast,
} from '@platform/client';
import { CAST_REGISTRY } from '@platform/schema/cast';
import { moduleFixSchema, recastSchema } from '@platform/schema/recast';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { ChangeTable } from '../authoring/ChangeTable';

import { StoryProposal } from './CurriculumChat';

import type { RequestRecastDto } from '@platform/client';
import type { CastEntry, CastId } from '@platform/schema/cast';
import type { PlanningStoryProposal } from '@platform/schema/chat';
import type { CastAssignment } from '@platform/schema/curriculum';
import type { Recast } from '@platform/schema/recast';

const nameOf = (id: CastId): string => CAST_REGISTRY[id].name;

/**
 * Replacing one of a module's cast in every built lesson, through review — CURR-48. Asked
 * from **Replace character** on an assignment, or in words at Module story & cast scope;
 * either way the proposal lists each lesson and scene it changes, and Apply writes drafts.
 * Words that are not a replacement draft the story instead, applied as the chat's — CURR-59.
 */
export function ModuleRecast({
  moduleId,
  revision,
  cast,
  people,
  replacing,
  onStopReplacing,
}: {
  moduleId: string;
  revision: number;
  cast: readonly CastAssignment[];
  people: readonly CastEntry[];
  replacing: CastAssignment | null;
  onStopReplacing: () => void;
}) {
  const queries = useQueryClient();
  const [request, setRequest] = useState('');
  const [character, setCharacter] = useState<CastId | ''>('');
  const [role, setRole] = useState<string | null>(null);
  const [proposal, setProposal] = useState<Recast | null>(null);
  const [story, setStory] = useState<PlanningStoryProposal | null>(null);
  const [storyApplied, setStoryApplied] = useState(false);

  const ask = useTrackedMutation('Propose recast', {
    mutationFn: async (body: Omit<RequestRecastDto, 'storyRevision'>) =>
      recastSchema.parse(
        (
          await requestRecast({
            path: { id: moduleId },
            body: { ...body, storyRevision: revision },
            throwOnError: true,
          })
        ).data,
      ),
    onSuccess: (recast) => {
      setProposal(recast);
      setRequest('');
      setCharacter('');
      setRole(null);
      onStopReplacing();
    },
  });
  const fix = useTrackedMutation('Fix the module with AI', {
    mutationFn: async (text: string) =>
      moduleFixSchema.parse(
        (
          await fixModule({
            path: { id: moduleId },
            body: { request: text, storyRevision: revision },
            throwOnError: true,
          })
        ).data,
      ),
    onSuccess: (answer) => {
      setProposal(answer.recast);
      setStory(answer.story);
      setStoryApplied(false);
      setRequest('');
    },
  });
  const acceptStory = useTrackedMutation('Accept the drafted module story', {
    mutationFn: async (proposed: PlanningStoryProposal) =>
      applyStoryProposal({ body: proposed, throwOnError: true }),
    onSuccess: async () => {
      setStoryApplied(true);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });
  const decide = useTrackedMutation('Decide on the recast', {
    mutationFn: async (verb: 'apply' | 'discard') => {
      if (proposal === null) return null;
      const call = verb === 'apply' ? applyRecast : discardRecast;
      return recastSchema.parse(
        (await call({ path: { id: proposal.id }, throwOnError: true })).data,
      );
    },
    onSuccess: async (recast) => {
      setProposal(recast?.status === 'APPLIED' ? recast : null);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
      await queries.invalidateQueries({ queryKey: ['review'] });
    },
  });

  const taken = new Set(cast.flatMap((one) => one.character ?? []));
  const choices = people.filter(
    (one) => !taken.has(one.id) && one.kind !== 'animal',
  );
  const busy =
    ask.isPending || fix.isPending || acceptStory.isPending || decide.isPending;
  const failure = ask.error ?? fix.error ?? acceptStory.error ?? decide.error;
  const pending =
    proposal?.status === 'PROPOSED' || (story !== null && !storyApplied);
  const from = proposal === null ? '' : nameOf(proposal.from.character);
  const to = proposal === null ? '' : nameOf(proposal.to.character);

  return (
    <Stack spacing={1} component="section" aria-label="Fix with AI">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h6" component="h3">
          Fix with AI
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Module story &amp; cast
        </Typography>
      </Stack>

      {replacing?.character !== undefined && !pending && (
        <Stack
          component="form"
          aria-label="Replace character"
          direction="row"
          spacing={1}
          sx={{ alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (character === '') return;
            ask.mutate({
              assignmentId: replacing.id,
              character,
              ...(role !== null &&
                role.trim() !== replacing.role && { role: role.trim() }),
            });
          }}
        >
          <Typography variant="body2" sx={{ pt: 1 }}>
            Replace {nameOf(replacing.character)} with
          </Typography>
          <TextField
            select
            size="small"
            label="Character"
            value={character}
            onChange={(event) => {
              setCharacter(event.target.value as CastId);
            }}
            sx={{ minWidth: '10rem' }}
          >
            {choices.map((one) => (
              <MenuItem key={one.id} value={one.id}>
                {one.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Role"
            value={role ?? replacing.role}
            onChange={(event) => {
              setRole(event.target.value);
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={busy || character === ''}
          >
            Propose
          </Button>
          <Button size="small" onClick={onStopReplacing}>
            Cancel
          </Button>
        </Stack>
      )}

      {!pending && replacing === null && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            label="What should change across this module?"
            placeholder="Replace Kofi with Betty as Supplier, or: build the story around Kofi"
            value={request}
            disabled={busy}
            onChange={(event) => {
              setRequest(event.target.value);
            }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy || request.trim() === ''}
            onClick={() => {
              fix.mutate(request.trim());
            }}
          >
            Fix with AI
          </Button>
        </Stack>
      )}

      {busy && <LinearProgress />}
      {failure !== null && (
        <Alert severity="error">{describeApiError(failure)}</Alert>
      )}

      {story !== null && (
        <StoryProposal
          proposal={story}
          outcome={storyApplied ? { state: 'applied' } : undefined}
          busy={busy}
          link={(lessonId) => lessonId}
          onAccept={() => {
            acceptStory.mutate(story);
          }}
          onDismiss={() => {
            setStory(null);
          }}
        />
      )}

      {proposal !== null && (
        <Stack spacing={2} component="section" aria-label="Recast proposal">
          {proposal.status === 'PROPOSED' ? (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Replace {from} with {to} as {proposal.to.role}.{' '}
                {proposal.lessons.length === 1
                  ? '1 lesson changes.'
                  : `${String(proposal.lessons.length)} lessons change.`}{' '}
                Nothing changes until you apply it.
              </Typography>
              <Typography variant="body2">Why: {proposal.why}</Typography>
            </Alert>
          ) : (
            <Alert severity="success">
              Applied. The drafts now seat {to} as {proposal.to.role}. Learners
              keep the published lessons until you publish the module.
            </Alert>
          )}
          {proposal.lacks.length > 0 && (
            <Alert severity="info" aria-label="What the new character lacks">
              {to} cannot play these, so the drafts drop them:
              <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                {proposal.lacks.map((one) => (
                  <li key={one}>{one}</li>
                ))}
              </Stack>
            </Alert>
          )}
          {proposal.lessons.map((lesson) => (
            <Stack
              key={lesson.lessonId}
              spacing={1}
              component="section"
              aria-label={lesson.title}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="subtitle2" component="h4">
                  {lesson.curriculumLessonId === ''
                    ? lesson.title
                    : `${lesson.curriculumLessonId} · ${lesson.title}`}
                </Typography>
                {lesson.outside && (
                  <Chip
                    size="small"
                    color="warning"
                    label="Outside this module: it shares a scene that changes"
                  />
                )}
                {lesson.published && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label="Published: learners keep it until the module publishes"
                  />
                )}
              </Stack>
              {lesson.changes.length > 0 && (
                <ChangeTable changes={lesson.changes} />
              )}
              {lesson.scenes.map((scene) => (
                <Stack key={scene.sceneId} spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Scene {scene.position}
                  </Typography>
                  <ChangeTable changes={scene.changes} />
                </Stack>
              ))}
            </Stack>
          ))}
          {proposal.status === 'PROPOSED' && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                onClick={() => {
                  decide.mutate('apply');
                }}
              >
                Apply
              </Button>
              <Button
                size="small"
                disabled={busy}
                onClick={() => {
                  decide.mutate('discard');
                }}
              >
                Cancel
              </Button>
            </Stack>
          )}
          {proposal.status !== 'PROPOSED' && (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => {
                setProposal(null);
              }}
            >
              Done
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}
